import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiTrendingUp, FiX, FiPlus } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({ base_currency: '', quote_currency: '', rate: '', as_of: '', source: '' });

const FXRates = () => {
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [curRes, rateRes] = await Promise.all([financeAPI.currencies.list(), financeAPI.fxRates.list()]);
      setCurrencies(curRes.data.results || curRes.data || []);
      setRates(rateRes.data.results || rateRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load FX rates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.fxRates.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to save FX rate.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiTrendingUp size={22} />FX Rates</h1>
          <p style={fs.pageSubtitle}>Append-only exchange rate history. Cross-currency journal entries look up the most recent rate as of their posting date.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Rate</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div><div style={fs.label}>Base Currency</div>
            <select required value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Quote Currency</div>
            <select required value={form.quote_currency} onChange={(e) => setForm({ ...form, quote_currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Rate</div><input required type="number" step="0.0000000001" placeholder="Quote per 1 base" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>As Of</div><input required type="datetime-local" value={form.as_of} onChange={(e) => setForm({ ...form, as_of: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Source</div><input placeholder="e.g. TCMB, RBZ, manual" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Save Rate'}</button>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : rates.length === 0 ? (
        <div style={fs.emptyState}><FiTrendingUp size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No FX rates recorded yet.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead><tr style={fs.tableHeadRow}>{['Pair', 'Rate', 'As Of', 'Source'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: i < rates.length - 1 ? '1px solid #111' : 'none' }}>
                  <td style={{ ...fs.td, color: fs.colors.textBright }}>{r.base_currency} → {r.quote_currency}</td>
                  <td style={fs.td}>{r.rate}</td>
                  <td style={fs.td}>{new Date(r.as_of).toLocaleString()}</td>
                  <td style={fs.td}>{r.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FXRates;
